import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatOption } from '@angular/material/core';

export interface SelectOption {
  value: number | string | null;
  viewValueTitle?: string;
  viewValueArray: Array<string>;
}

export interface AdditionalSelectOption {
  value: number;
  viewValue: string;
}

@Component({
  selector: 'ui-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent implements OnInit {
  @Input() public control = new FormControl<
    number | string | null | Array<number | string | null>
  >(null);
  @Input() public label = '';
  @Input() public isDisabled = false;
  @Input() public isMaterialLabel = true;
  @Input() public inputId = '';
  @Input() public isMultiSelect = false;
  @Input() public options: Array<SelectOption> = [];
  @Input() public showAllOption = false;
  @Input() public isMultiline = false;
  @Input() public additionalOption: AdditionalSelectOption | null = null;

  @ViewChild('allSelected') public allSelected: MatOption | undefined;

  public get selectedOptions(): string {
    if (
      this.additionalOption &&
      this.control.value === this.additionalOption.value
    ) {
      return this.additionalOption?.viewValue ?? 'undefined_label';
    }

    return this.options
      .filter(
        (option) =>
          this.control.value === option.value ||
          (Array.isArray(this.control.value) &&
            this.control.value.includes(option.value)),
      )
      .map((option) => {
        const title = option.viewValueTitle ?? '';
        const subtitle = option.viewValueArray.join(' ');
        if (this.isMultiline) {
          return `${title} ${subtitle}`.trim();
        }
        return subtitle;
      })
      .join(', ');
  }

  public ngOnInit(): void {
    if (this.isDisabled) {
      this.control.disable();
    }
  }

  public optionClicked(): void {
    this._markControl();

    if (
      this.isMultiSelect &&
      this.showAllOption &&
      Array.isArray(this.control.value)
    ) {
      if (this.allSelected?.selected) {
        this.allSelected?.deselect();
        return;
      }
      if (this.control.value.length === this.options.length) {
        this.allSelected?.select();
      }
    }
  }

  public toggleAllSelection(): void {
    this._markControl();

    if (this.allSelected?.selected) {
      this.control.patchValue([
        0,
        ...this.options
          .filter((option) => option.value !== null)
          .map((option) => option.value),
      ]);
      return;
    }

    this.control.patchValue([]);
  }

  private _markControl(): void {
    this.control.markAsTouched();
    this.control.markAsDirty();
    this.control.updateValueAndValidity();
  }
}
